import { type ReactNode } from 'react';
import { Box, Typography, IconButton, TextField, InputAdornment } from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { gwtStyles } from '../../config/theme';

interface AdminListPanelProps {
  title: string;
  /** Search value */
  search: string;
  /** Search change handler */
  onSearchChange: (value: string) => void;
  /** Refresh handler */
  onRefresh: () => void;
  /** Add new handler (optional — if not provided, Add button hidden) */
  onAdd?: () => void;
  /** Status bar content */
  statusText?: string;
  /** Extra toolbar content (action buttons, etc.) */
  toolbar?: ReactNode;
  /** The main list/grid content */
  children: ReactNode;
}

/**
 * Reusable admin list panel shell.
 * Provides title bar, search bar, toolbar, and content area.
 * Used by Users, Sites, HL7 Queue, Jobs, etc.
 */
export const AdminListPanel = ({
  title,
  search,
  onSearchChange,
  onRefresh,
  onAdd,
  toolbar,
  statusText,
  children,
}: AdminListPanelProps) => {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: gwtStyles.tabContent,
      }}
    >
      {/* Title bar */}
      <Box
        sx={{
          height: 32,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: gwtStyles.tabContent,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontSize: 13, fontWeight: 600, color: gwtStyles.tabTitle }}
        >
          {title}
        </Typography>
      </Box>

      {/* Search + toolbar bar */}
      <Box
        sx={{
          height: 36,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          gap: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <TextField
          size="small"
          placeholder="Search..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16 }} />
                </InputAdornment>
              ),
              sx: { fontSize: 12, height: 28 },
            },
          }}
          sx={{ width: 220 }}
        />
        {toolbar}
        <Box sx={{ flex: 1 }} />
        {onAdd && (
          <IconButton size="small" onClick={onAdd} title={`Add ${title}`} sx={{ width: 28, height: 28 }}>
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        <IconButton size="small" onClick={onRefresh} title="Refresh" sx={{ width: 28, height: 28 }}>
          <RefreshIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {children}
      </Box>

      {/* Status bar */}
      {statusText && (
        <Box
          sx={{
            height: 24,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            px: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
            {statusText}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
